import { DateTime, DateTimeFormatOptions, DateTimeFormatPreset } from 'luxon';
import { FALLBACK_LANGUAGE } from 'src/config/i18next';
import { useAppMeta } from 'src/core/provider/AppMeta';

export type FormatterOptions = Omit<Intl.NumberFormatOptions, 'style' | 'localeMatcher'> & ExtraOptions;
interface ExtraOptions {
    prefix?: string;
    postfix?: string;
}

export type FormatNumberFn = (value: number) => string;
export type MakeNumberFormatterFn = (options?: FormatterOptions) => FormatNumberFn;

export type MakeDateTimeFormatterFn = (options: DateTimeFormatPreset | DateTimeFormatOptions) => FormatDateTimeFn;
export type FormatDateTimeFn = (value: DateTime) => string;

function makeFormatter(locale: string | string[], options?: Intl.NumberFormatOptions & ExtraOptions): FormatNumberFn {
    const formatter = new Intl.NumberFormat(locale, options);

    return (value: number) => {
        return [options?.prefix, formatter.format(value), options?.postfix].filter(x => x !== undefined).join(' ');
    };
}

export function useNumberFormat(): MakeNumberFormatterFn {
    const appMeta = useAppMeta();

    return (options = {}) => makeFormatter([appMeta.selectedLocale, FALLBACK_LANGUAGE], options);
}

export function useUnitFormat(): MakeNumberFormatterFn {
    const appMeta = useAppMeta();

    return (options = {}) =>
        makeFormatter([appMeta.selectedLocale, FALLBACK_LANGUAGE], {
            ...options,
            style: 'unit',
        });
}

export function useCurrencyFormat(): MakeNumberFormatterFn {
    const appMeta = useAppMeta();

    return (options = {}) =>
        makeFormatter([appMeta.selectedLocale, FALLBACK_LANGUAGE], {
            ...options,
            style: 'currency',
        });
}

export function usePercentFormat(): MakeNumberFormatterFn {
    const appMeta = useAppMeta();

    return (options = {}) =>
        makeFormatter([appMeta.selectedLocale, FALLBACK_LANGUAGE], {
            ...options,
            style: 'percent',
        });
}

export function useDateTimeFormat(): MakeDateTimeFormatterFn {
    const appMeta = useAppMeta();

    return (options = DateTime.DATETIME_SHORT) => (dateTime: DateTime) =>
        dateTime.setLocale(appMeta.selectedLocale).toLocaleString(options);
}
